import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  enumGetValues,
  Id,
  throwObjectAlreadyExists,
  throwObjectNotFound,
} from '../../../common'
import { AddRoleOpts, Role, RoleType, RolesRepository, RolesService, UpdateRoleOpts } from './roles'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { Permissions } from '../../authorization'
import { includes, remove } from 'lodash'

enum EventType {
  ROLE_ADD = 'ROLE_ADD',
  ROLE_UPDATE = 'ROLE_UPDATE',
  ROLE_PERMISSIONS_ADD = 'ROLE_PERMISSIONS_ADD',
  ROLE_PERMISSIONS_DELETE = 'ROLE_PERMISSIONS_DELETE',
  ROLE_MERGE = 'ROLE_MERGE',
}

type AddRolePayload = {
  name: string
  description?: string
  permissions: string[]
  type: RoleType
}

type UpdateRolePayload = {
  name?: string
  description?: string
  permissions?: string[]
  type?: RoleType
}

interface DBRolesServiceOpts extends BaseDBServiceOpts<CoreDict> {
  rolesRepository: RolesRepository
}

export class DBRolesService extends BaseDBService<DBRolesServiceOpts, CoreDict> implements RolesService {
  constructor(opts: DBRolesServiceOpts) {
    super('DBRolesService', opts)
    this.logger.debug({}, '<65ef43e5> Initialized')
  }

  private async getRole(ctx: Context, tx: ROTransaction, id: Id): Promise<Role> {
    return (
      (await this.opts.rolesRepository.findById(tx, id)) ??
      throwObjectNotFound(`<d4af484a> ${this.translate(ctx, d => d.roles.notFoundByIdError, id)}`)
    )
  }

  async findAll(ctx: Context): Promise<Role[]> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_VIEW)
    return (
      await runWithROTransaction(this.opts.dbConnection, tx => {
        return this.opts.rolesRepository.findAll(tx)
      })
    ).filter(r => r.type !== RoleType.ANONYM)
  }

  async findByIdNonDeleted(ctx: Context, id: string): Promise<Role | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx => {
      return this.opts.rolesRepository.findByIdNonDeleted(tx, id)
    })
  }

  async getById(ctx: Context, id: string): Promise<Role> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx => this.getRole(ctx, tx, id))
  }

  async add(ctx: Context, opts: AddRoleOpts): Promise<Id> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_MANAGE)
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      ;(await this.opts.rolesRepository.findByName(tx, opts.name.trim())) &&
        throwObjectAlreadyExists(`<9585e6aa> ${this.translate(ctx, d => d.roles.roleExistsError, opts.name)}`)

      const id = opts.id ?? this.opts.idGenerator.generate()

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId: id,
        type: EventType.ROLE_ADD,
        payload: <AddRolePayload>{
          name: opts.name.trim(),
          description: opts.description?.trim(),
          permissions: opts.permissions,
          type: opts.type,
        },
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async update(ctx: Context, id: Id, opts: UpdateRoleOpts): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_MANAGE)

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.ROLE_UPDATE,
        payload: <UpdateRolePayload>{
          name: opts.name?.trim(),
          description: opts.description?.trim(),
          permissions: opts.permissions,
        },
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async addPermissions(ctx: Context, id: string, permissions: string[]): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_MANAGE)

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.ROLE_PERMISSIONS_ADD,
        payload: permissions,
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async deletePermissions(ctx: Context, id: string, permissions: string[]): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.ROLES_MANAGE)

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.ROLE_PERMISSIONS_DELETE,
        payload: permissions,
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

export class RolesModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(private opts: { rolesRepository: RolesRepository; eventCreator: ModelEventCreator }) {}

  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const role =
      (await this.opts.rolesRepository.findById(tx, entityId)) ??
      throwObjectNotFound(`<c28353d4> Role not found by id=${entityId}`)
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.ROLE_MERGE,
      payload: <UpdateRolePayload>{
        type: role.type,
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      },
    })
  }

  async process(context: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type) {
      case EventType.ROLE_ADD: {
        const p = event.payload as AddRolePayload
        await this.opts.rolesRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          name: p.name,
          permissions: p.permissions,
          description: p.description,
          type: p.type,
        })
        return true
      }
      case EventType.ROLE_MERGE:
      case EventType.ROLE_UPDATE: {
        const p = event.payload as UpdateRolePayload
        const role =
          (await this.opts.rolesRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(`<37753f6e> Role not found by id=${event.entityId}`)
        await this.opts.rolesRepository.save(tx, {
          ...role,
          type: p.type ?? role.type,
          name: p.name ?? role.name,
          permissions: p.permissions ?? role.permissions,
          description: p.description ?? role.description,
        })
        return true
      }
      case EventType.ROLE_PERMISSIONS_ADD: {
        const perm = event.payload as string[]
        const role =
          (await this.opts.rolesRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(`<c75ef2d5> Role not found by id=${event.entityId}`)

        await this.opts.rolesRepository.save(tx, {
          ...role,
          permissions: [...new Set(role.permissions.concat(perm))],
        })
        return true
      }
      case EventType.ROLE_PERMISSIONS_DELETE: {
        const perm = event.payload as string[]
        const role =
          (await this.opts.rolesRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(`<a6c783a5> Role not found by id=${event.entityId}`)

        remove(role.permissions, p => includes(perm, p))
        await this.opts.rolesRepository.save(tx, {
          ...role,
          permissions: role.permissions,
        })
        return true
      }
      default: {
        return false
      }
    }
  }
}
